import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import AIBubble from '@/storybook/components/AiChatDrawer/components/AIBubble/AIBubble';
import Alert from '@/storybook/components/Alert/Alert';
import Button from '@/storybook/components/Button/Button';
import type { ChatbotFileEvent, ChatbotMessage, ChatbotMessageFilter } from '@/models/chatbot';
import type { ChatbotError } from '../store/useChatbotStore';
import ChatMessage, { AiMessageContent } from '../ChatMessage/ChatMessage';
import StoppedResponseDivider from '../StoppedResponseDivider/StoppedResponseDivider';
import TypingIndicator from './TypingIndicator/TypingIndicator';
import styles from './ChatDrawer.module.css';
import { CHAT_DRAWER_TEST_IDS } from './testIds';

interface ChatConversationContentProps {
  messages: ReadonlyArray<ChatbotMessage>;
  isStreaming: boolean;
  streamingContent: string;
  streamingFiles: ChatbotFileEvent[];
  streamingReasoningSteps: string[];
  streamingPromptFilters: ReadonlyArray<ChatbotMessageFilter>;
  error: ChatbotError | null;
  errorMessage?: string;
  onDismissError: () => void;
  onRetry: () => void;
}

const hasRenderableMessage = (message: ChatbotMessage): boolean =>
  message.content.length > 0 || (message.files?.length ?? 0) > 0 || (message.reasoningSteps?.length ?? 0) > 0;

const ChatConversationContent: FC<ChatConversationContentProps> = ({
  messages,
  isStreaming,
  streamingContent,
  streamingFiles,
  streamingReasoningSteps,
  streamingPromptFilters,
  error,
  errorMessage,
  onDismissError,
  onRetry,
}) => {
  const { t } = useTranslation();
  const hasStreamingPreview =
    streamingContent.length > 0 || streamingFiles.length > 0 || streamingReasoningSteps.length > 0;

  return (
    <>
      {messages.map(message => (
        <Fragment key={message.id}>
          {hasRenderableMessage(message) ? <ChatMessage message={message} /> : null}
          {message.interrupted ? <StoppedResponseDivider variant={message.interrupted} /> : null}
        </Fragment>
      ))}
      {isStreaming && hasStreamingPreview ? (
        <div data-testid={CHAT_DRAWER_TEST_IDS.streamingMessage}>
          <AIBubble hideActions>
            <div className={styles.streamingContent}>
              <AiMessageContent
                content={streamingContent}
                files={streamingFiles}
                promptFilters={streamingPromptFilters}
                reasoningSteps={streamingReasoningSteps}
                trailingContent={<TypingIndicator />}
              />
            </div>
          </AIBubble>
        </div>
      ) : null}
      {isStreaming && !hasStreamingPreview ? (
        <AIBubble hideActions>
          <div className={styles.streamingContent}>
            <TypingIndicator />
          </div>
        </AIBubble>
      ) : null}
      {error && !isStreaming ? (
        <div className={styles.errorBubble}>
          <AIBubble hideActions className={styles.errorBubbleInner} contentClassName={styles.errorBubbleContent}>
            <Alert
              variant="error"
              message={errorMessage ?? error.message}
              dismissible
              onClose={onDismissError}
              action={
                error.retryable ? (
                  <Button
                    variant="text"
                    size="S"
                    className={styles.retryLink}
                    onClick={onRetry}
                    data-testid={CHAT_DRAWER_TEST_IDS.retryButton}
                  >
                    {t('components.aiChatDrawer.chatMessage.retry')}
                  </Button>
                ) : undefined
              }
              className={styles.errorAlert}
              data-testid={CHAT_DRAWER_TEST_IDS.errorAlert}
            />
          </AIBubble>
        </div>
      ) : null}
    </>
  );
};

export default ChatConversationContent;
